import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class ZettleOrder extends ModelObject<ZettleOrder> {

    public version: number = 0;
    /**
    * The Zettle unique id for this order (GUID).
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public zettleOrderId: string = "";
    /**
    * The date and time this order was created in Zettle.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The order name as it would appear in Zettle (Source + Purchase Number).
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public orderName: string = "";
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    private _brewmanCreditNoteId: Guid | null = null;
    /**
    * The Brewman credit note id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanCreditNoteId(): Guid | null { return this._brewmanCreditNoteId }
    public set brewmanCreditNoteId(value: Guid | null) {
      this._brewmanCreditNoteId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.zettleOrderId = jsonSerializedObject.zettleOrderId;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.orderName = jsonSerializedObject.orderName;
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.brewmanCreditNoteId = jsonSerializedObject.brewmanCreditNoteId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanCreditNoteId);
          this.errorMessage = jsonSerializedObject.errorMessage;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.zettleOrderId = this.zettleOrderId;
        toRet.createdAt = this.createdAt.toString();
        toRet.orderName = this.orderName;
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.brewmanCreditNoteId = this.brewmanCreditNoteId == null ? null : this.brewmanCreditNoteId.toString();
        toRet.errorMessage = this.errorMessage;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "ZettleOrder.BrewmanOrderId cannot be empty"},
            {field: "brewmanCreditNoteId", rule: (v: any) => v == null || !v.isEmpty() || "ZettleOrder.BrewmanCreditNoteId cannot be empty"},
    ];
}


export class FilteredZettleOrdersResult extends ModelObject<FilteredZettleOrdersResult> {

    public matchingCount: number = 0;
    public zettleOrders: (ZettleOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.zettleOrders = jsonSerializedObject.zettleOrders.map((v: any) => new ZettleOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.zettleOrders = this.zettleOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "zettleOrders", rule: (v: any) => !!v || "zettleOrders is required"},
            {field: "zettleOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ZettleOrder[]).reduce((acc: Array<string>, curr: ZettleOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ZettleOrderFilter extends ModelObject<ZettleOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class ZettleOrderService extends ServiceBase {

  public async getZettleOrdersByFilter(filter: ZettleOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredZettleOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredZettleOrdersResult(response.results);;
    return this.makeJsonRequest("zettleOrder/v1/getZettleOrdersByFilter", req, false, extractor, opts);
  };
}

export const zettleOrderService = new ZettleOrderService();